import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  Paper,
  Switch,
  FormControlLabel,
  Collapse,
  Button,
  Chip,
  Tooltip,
  IconButton
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  ColorLens as ColorLensIcon,
  CropRotate as CropRotateIcon
} from "@mui/icons-material";
import { convertDateToWords } from "../utils/dateToWords";

const TERMS = [
  { key: "first Term", label: "Term-I" },
  { key: "Second Term", label: "Term-II" },
  { key: "Final Term", label: "Final Exam" },
];

const toCamelCase = (str) => {
  if (!str) return "";
  return str
    ?.toLowerCase()
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
};

// Student Info Fields Configuration
const STUDENT_INFO_FIELDS = {
  photo: { label: "Photo", defaultVisible: true },
  rollNo: { label: "Roll No", defaultVisible: false },
  name: { label: "Student's Name", defaultVisible: true },
  fullname: { label: "Student's full Name", defaultVisible: true },
  father: { label: "Father's name", defaultVisible: true },
  mother: { label: "Mother's name", defaultVisible: true },
  apaarId: { label: "Apaar ID", defaultVisible: true },
  aadhaarNumber: { label: "Aadhaar Number", defaultVisible: false },
  studied: { label: "Studied (Class)", defaultVisible: true },
  admissionNo: { label: "Admission No.", defaultVisible: true },
  casteCategory: { label: "Category/Caste", defaultVisible: false },
  standard: { label: "Standard", defaultVisible: false },
  dob: { label: "Date of Birth", defaultVisible: true },
  dobInWords: { label: "DOB (In Words)", defaultVisible: false },
  heightWeight: { label: "Height/Weight", defaultVisible: false },
  contact: { label: "Contact Number", defaultVisible: false },
  address: { label: "Address", defaultVisible: false },
};

function ProfileLine({ label, value, isVisible }) {
  if (!isVisible) return null;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "180px auto",
        mb: 0.3,
        gap: 0.5,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: "0.85rem" }}>
        {label} :
      </Typography>
      <Typography sx={{ fontSize: "0.85rem" }}>{value}</Typography>
    </Box>
  );
}

// Renders the full 2-page block for ONE student.
function StudentMarksheet({
  studentData,
  school,
  isLast,
  themeColor,
  selectedTerm,
  isLandscape,
  fieldVisibility
}) {
  // Extract marksheets from student data
  const marksheets = studentData?.marksheets || [];
  const profile = studentData || {};

  // Get classroom info from the first marksheet
  const classroomInfo = marksheets[0]?.classRoom || profile?.classRoom || {}

  // extract unique subjects across this student's marksheets
  const subjects = useMemo(() => {
    const allMarks = marksheets.flatMap((r) => r.marks || []);
    const unique = [
      ...new Map(allMarks.map((m) => [m.subjectName?.trim(), m])).values(),
    ];
    return unique.sort((a, b) =>
      a.subjectName?.toLowerCase().localeCompare(b.subjectName?.toLowerCase())
    );
  }, [marksheets]);

  const findTerm = (examType) =>
    marksheets.find((r) =>
      r.examType?.trim().toLowerCase().includes(examType?.trim().toLowerCase())
    );

  const findMark = (examType, subject) => {
    const term = findTerm(examType);
    return term?.marks?.find(
      (s) => s.subjectName?.trim().toLowerCase() === subject?.trim().toLowerCase()
    );
  };

  const getTotals = (examType) => {
    const term = findTerm(examType);
    if (!term || !term.marks) return { max: 0, obt: 0, grade: "-" };
    const max = term.marks.reduce((sum, s) => sum + (s.maxMark || 0), 0);
    const obt = term.marks.reduce((sum, s) => sum + (s.marksObtained || 0), 0);
    return { max, obt, grade: term.grade || "-" };
  };

  // Only the term(s) currently selected in the filter get rendered.
  const visibleTerms =
    selectedTerm === "all"
      ? TERMS
      : TERMS.filter((t) => t.key.toLowerCase() === selectedTerm.toLowerCase());

  const totalsByTerm = useMemo(() => {
    const map = {};
    TERMS.forEach((t) => {
      map[t.key] = getTotals(t.key);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marksheets]);

  // Use the "Final" term record for result/remarks/percentage.
  const finalRecord = findTerm("Final") || marksheets[marksheets.length - 1] || {};

  // Profile info comes from the student data
  const profileRecord = profile;

  const borderColor = themeColor;
  const headerBg = alpha(themeColor, 0.14);
  const totalsBg = alpha(themeColor, 0.06);

  const cellPadding = isLandscape ? "4px" : "8px";
  const cellSx = { border: `1px solid ${borderColor}`, padding: cellPadding };
  const headCellSx = { ...cellSx, background: headerBg };

  const fontSize = isLandscape ? "0.7rem" : "0.85rem";
  const headerFontSize = isLandscape ? "0.75rem" : "0.9rem";

  // Render student info field
  const renderStudentField = (fieldKey) => {
    if (!fieldVisibility[fieldKey]) return null;

    const field = STUDENT_INFO_FIELDS[fieldKey];
    let value = '';

    switch (fieldKey) {
      case 'apaarId':
        value = profile?.apaarId || '';
        break;
      case 'fullname':
        value = toCamelCase(`${profile?.name || ''} ${profile?.father || ''} ${profile?.last || ''}`.trim());
        break;
      case 'name':
        value = toCamelCase(`${profile?.name || ''} ${profile?.last || ''}`.trim());
        break;
      case 'father':
        value = toCamelCase(`${profile?.father || ''} ${profile?.last || ''}`.trim());
        break;
      case 'mother':
        value = toCamelCase(`${profile?.mother || ''} ${profile?.last || ''}`.trim());
        break;
      case 'studied':
        value = `${classroomInfo?.standard || ''} | ${classroomInfo?.section || ''}`;
        break;
      case 'admissionNo':
        value = profile?.id || profile?.studentId || '';
        break;
      case 'casteCategory':
        value = `${profile?.category || ''} | caste: ${profile?.caste || ''}`;
        break;
      case 'dob':
        value = profile?.birthday ? new Date(profile.birthday).toLocaleDateString("en-GB") : '';
        break;
      case 'dobInWords':
        value = profile?.birthday ? convertDateToWords(profile.birthday) : '';
        break;
      case 'heightWeight':
        return (
          <Box key={fieldKey} display={'flex'} justifyContent={'space-between'} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="body2" component="span" fontWeight={700} sx={{ fontSize: "0.8rem" }}>{field.label}</Typography>
            <TextField
              variant="standard"
              placeholder="Height/Weight"
              size="small"
              slotProps={{
                input: {
                  disableUnderline: true,
                },
              }}
            />
          </Box>
        );
      case 'photo':
        return profile?.photo ? (
          <Box key={fieldKey} display="flex" justifyContent="center" my={0.5}>
            <img
              src={profile.photo}
              alt="Student"
              style={{ width: isLandscape ? 70 : 100, height: isLandscape ? 70 : 100, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${borderColor}` }}
              onError={(e) => e.target.style.display = 'none'}
            />
          </Box>
        ) : null;
      case 'aadhaarNumber':
        value = profile?.aadhaar || '';
        break;
      case 'contact':
        value = profile?.mobile || '';
        break;
      case 'standard':
        value = `${classroomInfo?.standard || ''} | ${classroomInfo?.section || ''}`;
        break;
      case 'address':
        value = toCamelCase(`${profile?.address || ''} ${profile?.city || ''} ${profile?.state || ''}`.trim()) || '';
        break;
      case 'rollNo':
        value = profile?.rollNumber || '';
        break;
      default:
        return null;
    }

    if (!value) return null;

    return (
      <Box
        key={fieldKey}
        sx={{
          display: "grid",
          gridTemplateColumns: isLandscape ? "280px auto" : "280px auto",
          mb: 0.3,
          gap: 1,
          alignItems: "center"
        }}
      >
        <Typography sx={{ fontWeight: "bold", fontSize: isLandscape ? "0.75rem" : "0.85rem" }} component="span">
          {field.label} :
        </Typography>
        <Typography sx={{ fontSize: isLandscape ? "0.75rem" : "0.85rem" }} component="span">
          {value}
        </Typography>
      </Box>
    );
  };

  const visibleFields = Object.keys(STUDENT_INFO_FIELDS)
    .filter(key => fieldVisibility[key]);

  // Get class info from first marksheet
  const classInfo = marksheets[0]?.classRoom || profile?.classRoom || {};

  return (
    <>
      {/* PAGE 1: Student Profile */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          marginTop: 2,
          p: isLandscape ? 2 : 3,
          background: "#fff",
          fontFamily: "Arial",
          maxWidth: isLandscape ? "1100px" : "800px",
          margin: "auto",
          border: `2px solid ${borderColor}`,
          borderRadius: "12px",
          backgroundColor: "#fff",
        }}
      >
        <Box sx={{ p: 1, width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 1,
              mt: 2,
            }}
          >
            <img
              src={school?.logoUrl}
              alt="School Logo"
              style={{ width: isLandscape ? 80 : 100, height: isLandscape ? 70 : 90, marginTop: 10 }}
              onError={(e) => e.target.style.display = 'none'}
            />

            <Typography variant={isLandscape ? "h5" : "h4"} align="center" fontWeight="bold" sx={{ color: borderColor }}>
              {toCamelCase(school?.schoolName)}
            </Typography>
            <Typography variant="subtitle1" align="center" sx={{ fontSize: isLandscape ? "0.8rem" : "1rem" }}>
              {toCamelCase(
                [
                  school?.addressDTO?.addressLine1,
                  school?.addressDTO?.city,
                  school?.addressDTO?.pincode,
                  school?.addressDTO?.state,
                ].filter(Boolean).join(", ")
              )}
            </Typography>
            <Typography variant="subtitle1" align="center" fontWeight="bold" sx={{ fontSize: isLandscape ? "0.85rem" : "1rem" }}>
              PROGRESS REPORT SESSION - {classInfo?.academicYear || profileRecord?.academicYear || 'N/A'}
            </Typography>

            {/* Photo */}
            {fieldVisibility.photo && profileRecord?.photo && (
              <img
                src={profileRecord.photo}
                alt="Student"
                style={{ width: isLandscape ? 80 : 100, height: isLandscape ? 80 : 100, marginTop: 10, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${borderColor}` }}
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
          </Box>

          <Typography variant="subtitle1" align="center" fontWeight="bold" sx={{ bgcolor: headerBg, py: 0.5, borderRadius: 1, fontSize: isLandscape ? "0.85rem" : "1rem" }}>
            {"STUDENT PROFILE"}
          </Typography>
          <Divider sx={{ mb: 2, borderColor }} />

          {/* Student Info */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              px: 1,
            }}
          >
            {visibleFields
              .filter(key => key !== 'photo')
              .map(key => renderStudentField(key))}
          </Box>
        </Box>
      </Box>

      <div className="pagebreak"></div>

      {/* PAGE 2: Marksheet */}
      <Box
        sx={{
          p: isLandscape ? 2 : 3,
          background: "#fff",
          fontFamily: "Arial",
          maxWidth: isLandscape ? "1100px" : "800px",
          margin: "auto",
          border: `2px solid ${borderColor}`,
          borderRadius: "12px",
          backgroundColor: "#fff",
        }}
        style={{
          backgroundImage: `url(${school?.logoUrl})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundColor: "rgb(255 255 255 / 87%)",
          backgroundBlendMode: "lighten",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "column",
            alignItems: "center",
            mb: 1,
            mt: 0,
          }}
        >
          <Typography variant={isLandscape ? "h6" : "h5"} align="center" fontWeight="bold" sx={{ color: borderColor }}>
            {toCamelCase(school?.schoolName)}
          </Typography>
          <Typography variant="subtitle1" align="center" sx={{ fontSize: isLandscape ? "0.8rem" : "1rem" }}>
            {toCamelCase(
              [school?.addressLine2, school?.city, school?.postalCode, school?.state]
                .filter(Boolean)
                .join(", ")
            )}
          </Typography>
          <Typography align="center" sx={{ fontWeight: "bold", my: 1, fontSize: isLandscape ? "0.85rem" : "1rem" }}>
            PROGRESS REPORT SESSION - {classInfo?.academicYear || profileRecord?.academicYear || 'N/A'}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2, mt: 1, borderColor }} />

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
          <Typography variant="body2" sx={{ fontSize: fontSize }}>
            <strong>Student's Name: </strong>{" "}
            {toCamelCase(profileRecord?.studentName || `${profileRecord?.name || ''} ${profileRecord?.last || ''}`.trim())}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: fontSize }}>
            <strong>Standard: </strong>
            {classInfo?.standard} | {classInfo?.section}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: fontSize }}>
            <strong>Roll No.: </strong> {profileRecord?.rollNumber || classInfo?.rollNumber}
          </Typography>
        </Box>
        <Divider sx={{ m: 1, borderColor }} />

        {/* Marks Table */}
        <Box sx={{ py: 1, mb: 2, overflowX: 'auto' }}>
          <Table size={isLandscape ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headCellSx }} rowSpan={2} align="center">
                  <strong style={{ fontSize: headerFontSize }}>Subject</strong>
                </TableCell>

                {visibleTerms.map((term) => (
                  <TableCell key={term.key} sx={{ ...headCellSx }} colSpan={4} align="center">
                    <strong style={{ fontSize: headerFontSize }}>{term.label}</strong>
                  </TableCell>
                ))}
              </TableRow>

              <TableRow>
                {visibleTerms.flatMap((term) => [
                  <TableCell key={`max-${term.key}`} sx={headCellSx} align="center" style={{ fontSize: fontSize }}>
                    Max
                  </TableCell>,
                  <TableCell key={`min-${term.key}`} sx={headCellSx} align="center" style={{ fontSize: fontSize }}>
                    Min
                  </TableCell>,
                  <TableCell key={`obt-${term.key}`} sx={headCellSx} align="center" style={{ fontSize: fontSize }}>
                    Obt
                  </TableCell>,
                  <TableCell key={`grade-${term.key}`} sx={headCellSx} align="center" style={{ fontSize: fontSize }}>
                    Grade
                  </TableCell>,
                ])}
              </TableRow>
            </TableHead>

            <TableBody>
              {subjects.map((sub, i) => (
                <TableRow key={`subject-${i}`}>
                  <TableCell sx={cellSx} style={{ fontSize: fontSize }}>
                    {toCamelCase(sub.subjectName)}
                  </TableCell>

                  {visibleTerms.flatMap((term) => {
                    const mark = findMark(term.key, sub.subjectName);
                    return [
                      <TableCell key={`${term.key}-max-${i}`} sx={cellSx} align="center" style={{ fontSize: fontSize }}>
                        {mark?.maxMark || 100}
                      </TableCell>,
                      <TableCell key={`${term.key}-min-${i}`} sx={cellSx} align="center" style={{ fontSize: fontSize }}>
                        {Math.round(((mark?.maxMark || 100) * 34) / 100)}
                      </TableCell>,
                      <TableCell key={`${term.key}-obt-${i}`} sx={cellSx} align="center" style={{ fontSize: fontSize }}>
                        {mark?.marksObtained || "-"}
                      </TableCell>,
                      <TableCell key={`${term.key}-grade-${i}`} sx={cellSx} align="center" style={{ fontSize: fontSize }}>
                        {mark?.grade || "-"}
                      </TableCell>,
                    ];
                  })}
                </TableRow>
              ))}

              {/* Totals Row */}
              <TableRow sx={{ background: totalsBg }}>
                <TableCell sx={cellSx}>
                  <strong style={{ fontSize: headerFontSize }}>Total Marks</strong>
                </TableCell>

                {visibleTerms.flatMap((term) => {
                  const t = totalsByTerm[term.key];
                  return [
                    <TableCell key={`${term.key}-tmax`} sx={cellSx} align="center" style={{ fontSize: fontSize }}>
                      {t.max}
                    </TableCell>,
                    <TableCell key={`${term.key}-tmin`} sx={cellSx} />,
                    <TableCell key={`${term.key}-tobt`} sx={cellSx} align="center" style={{ fontSize: fontSize }}>
                      {t.obt}
                    </TableCell>,
                    <TableCell key={`${term.key}-tgrade`} sx={cellSx} align="center" style={{ fontSize: fontSize }}>
                      {t.grade}
                    </TableCell>,
                  ];
                })}
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Result Section */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
          <Typography variant="body2" sx={{ fontSize: fontSize }}>
            <strong>Result:</strong> {finalRecord?.result || 'N/A'}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ fontSize: fontSize }}>
            <b>Remarks: </b>
            {finalRecord?.remarks || 'N/A'}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: fontSize }}>
            <strong>Percentage:</strong> {finalRecord?.percentage || 'N/A'}%
          </Typography>
          <Typography variant="body2" sx={{ fontSize: fontSize }}>
            <strong>Grade:</strong> {finalRecord?.grade || 'N/A'}
          </Typography>
        </Box>

        {/* Footer */}
        <Box my={6} display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Typography variant="body2" sx={{ fontSize: fontSize }}>Signature: ___________________</Typography>
          <Typography variant="body2" sx={{ fontSize: fontSize }}>Class Teacher Sign.: ___________________</Typography>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: fontSize }}>
              Principal/HeadMaster
            </Typography>
            <Typography variant="body2" sx={{ fontSize: fontSize }}>Seal/Sign.: ___________________</Typography>
          </Box>
        </Box>
      </Box>

      {/* page break between students (not after the very last one) */}
      {!isLast && <div className="pagebreak"></div>}
    </>
  );
}

const SinglePageMarksheetReceiptBulk = ({ receiptData, school, setTemID }) => {
  // receiptData.data is now an array of student objects
  const students = receiptData.data || [];
  const receiptId = `marksheet`;

  // --- Customization controls ---
  const [themeColor, setThemeColor] = useState("#000000");
  const [selectedTerm, setSelectedTerm] = useState("all");
  const [showSettings, setShowSettings] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [fieldVisibility, setFieldVisibility] = useState(() => {
    const saved = localStorage.getItem('singlePageBulkMarksheetFieldVisibility');
    if (saved) {
      return JSON.parse(saved);
    }
    const defaults = {};
    Object.entries(STUDENT_INFO_FIELDS).forEach(([key, field]) => {
      defaults[key] = field.defaultVisible;
    });
    return defaults;
  });

  useEffect(() => {
    if (setTemID) {
      setTemID(receiptId);
    }
  }, [receiptData, setTemID, receiptId]);

  // Save visibility settings
  useEffect(() => {
    localStorage.setItem('singlePageBulkMarksheetFieldVisibility', JSON.stringify(fieldVisibility));
  }, [fieldVisibility]);

  // Add landscape styles to document
  useEffect(() => {
    if (isLandscape) {
      const style = document.createElement('style');
      style.id = 'landscape-style-single-bulk';
      style.innerHTML = `
        @media print {
          @page {
            size: landscape;
            margin: 8mm;
          }
          .pagebreak {
            page-break-after: always;
          }
        }
      `;
      document.head.appendChild(style);
    } else {
      const existingStyle = document.getElementById('landscape-style-single-bulk');
      if (existingStyle) {
        existingStyle.remove();
      }
    }
    return () => {
      const existingStyle = document.getElementById('landscape-style-single-bulk');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [isLandscape]);

  // Toggle field visibility
  const toggleFieldVisibility = (fieldKey) => {
    setFieldVisibility(prev => ({
      ...prev,
      [fieldKey]: !prev[fieldKey]
    }));
  };

  // Toggle all fields
  const toggleAllFields = (visible) => {
    const newState = {};
    Object.keys(STUDENT_INFO_FIELDS).forEach(key => {
      newState[key] = visible;
    });
    setFieldVisibility(newState);
  };

  // Get visibility count
  const visibleCount = Object.values(fieldVisibility).filter(v => v).length;
  const totalFields = Object.keys(STUDENT_INFO_FIELDS).length;

  // Toggle landscape mode
  const toggleLandscape = () => {
    setIsLandscape(!isLandscape);
  };

  const containerMaxWidth = isLandscape ? "1100px" : "800px";

  return (
    <Box id={receiptId}>
      {/* Controls Panel */}
      <Paper
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          justifyContent: "center",
          maxWidth: containerMaxWidth,
          margin: "auto",
          mb: 2,
          p: 1.5,
          "@media print": { display: "none" },
        }}
        elevation={2}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" fontWeight={600} fontSize="0.8rem" component="span">
            <ColorLensIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
            Color:
          </Typography>
          <input
            type="color"
            value={themeColor}
            onChange={(e) => setThemeColor(e.target.value)}
            style={{
              width: 32,
              height: 28,
              padding: 0,
              border: "1px solid #ccc",
              borderRadius: 4,
              cursor: "pointer",
            }}
          />
        </Box>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="term-filter-label" sx={{ fontSize: "0.8rem" }}>Term</InputLabel>
          <Select
            labelId="term-filter-label"
            label="Term"
            value={selectedTerm}
            onChange={(e) => setSelectedTerm(e.target.value)}
            sx={{ fontSize: "0.8rem" }}
          >
            <MenuItem value="all">All Terms</MenuItem>
            {TERMS.map((t) => (
              <MenuItem key={t.key} value={t.key}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          size="small"
          variant={isLandscape ? "contained" : "outlined"}
          startIcon={<CropRotateIcon />}
          onClick={toggleLandscape}
          sx={{ fontSize: "0.75rem", textTransform: "none" }}
        >
          {isLandscape ? "Landscape" : "Portrait"}
        </Button>

        <Button
          size="small"
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => setShowSettings(!showSettings)}
          sx={{ fontSize: "0.75rem", textTransform: "none" }}
        >
          Customize
        </Button>

        <Tooltip title={`${visibleCount}/${totalFields} fields visible`}>
          <Chip
            size="small"
            label={`${visibleCount}/${totalFields}`}
            color="primary"
            variant="outlined"
            sx={{ height: 28 }}
          />
        </Tooltip>
      </Paper>

      {/* Settings Panel */}
      <Collapse in={showSettings}>
        <Paper
          sx={{
            maxWidth: containerMaxWidth,
            margin: "auto",
            mb: 2,
            p: 2,
            bgcolor: '#f8f9fa',
            "@media print": { display: "none" },
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2" fontWeight="600" fontSize="0.85rem" component="span">
              Toggle Student Info Fields
            </Typography>
            <Box display="flex" gap={1}>
              <Button size="small" onClick={() => toggleAllFields(true)} sx={{ fontSize: "0.75rem" }}>
                Show All
              </Button>
              <Button size="small" onClick={() => toggleAllFields(false)} sx={{ fontSize: "0.75rem" }}>
                Hide All
              </Button>
            </Box>
          </Box>
          <Grid container spacing={0.5}>
            {Object.entries(STUDENT_INFO_FIELDS).map(([key, field]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={fieldVisibility[key] || false}
                      onChange={() => toggleFieldVisibility(key)}
                      size="small"
                      color="primary"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={0.5} component="span">
                      <Typography variant="caption" fontSize="0.7rem" component="span">{field.label}</Typography>
                      {fieldVisibility[key] ? (
                        <VisibilityIcon fontSize="small" color="primary" sx={{ fontSize: 14 }} />
                      ) : (
                        <VisibilityOffIcon fontSize="small" color="disabled" sx={{ fontSize: 14 }} />
                      )}
                    </Box>
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Collapse>

      {/* Render each student's marksheet */}
      {students.map((student, idx) => (
        <StudentMarksheet
          key={student?.id ?? student?.studentId ?? idx}
          studentData={student}
          school={school}
          isLast={idx === students.length - 1}
          themeColor={themeColor}
          selectedTerm={selectedTerm}
          isLandscape={isLandscape}
          fieldVisibility={fieldVisibility}
        />
      ))}

      {/* Empty state */}
      {students.length === 0 && (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" color="textSecondary">
            No student data available.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SinglePageMarksheetReceiptBulk;